"use client";
import { MentionTextarea } from "@cmp-src/form/MentionTextarea/MentionTextarea";
import { Category, ContentBlock } from "@cmp-src/form/MentionTextarea/types";
import { cn } from "@gettactic/helpers";
import type { FileUIPart } from "ai";
import { ArrowUp, ImagePlus, X } from "lucide-react";
import React, { type HTMLAttributes } from "react";
import {
  CHAT_PHOTO_TYPES,
  chatPhotosError,
  readChatPhoto
} from "@/features/tessa/components/utils/chatPhotos";

type PromptInputProps = HTMLAttributes<HTMLFormElement>;
const PromptInput = ({ className, ...props }: PromptInputProps) => (
  <form
    className={cn(
      "w-full divide-y overflow-hidden rounded-xl border bg-background shadow-sm",
      className
    )}
    {...props}
  />
);

type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;
const PromptInputTools = ({ className, ...props }: PromptInputToolsProps) => (
  <div
    className={cn(
      "flex items-center gap-1",
      "[&_button:first-child]:rounded-bl-xl",
      className
    )}
    {...props}
  />
);

type PromptInputToolbarProps = HTMLAttributes<HTMLDivElement>;
const PromptInputToolbar = ({
  className,
  ...props
}: PromptInputToolbarProps) => (
  <div
    className={cn("flex items-center justify-between p-1", className)}
    {...props}
  />
);

export interface ChatBotPromptInputProps {
  status?: string;
  handleSubmit: (
    bubbleText: string,
    aiText: string,
    photos: FileUIPart[]
  ) => void;
  className?: string;
  showSuggestions?: boolean;
  placeholder?: string;
  isCompact?: boolean;
  inputSuggestions?: Category[];
  initialValue?: ContentBlock[];
}

const suggestions = [
  "Book a desk",
  "Report a problem",
  "Invite a visitor",
  "Schedule a catered lunch"
];

export const ChatBotPromptInput: React.FC<ChatBotPromptInputProps> = ({
  status,
  handleSubmit,
  className,
  showSuggestions,
  placeholder = "Ask anything",
  isCompact = false,
  inputSuggestions,
  initialValue
}) => {
  const [text, setText] = React.useState<string>("");
  const [input, setInput] = React.useState<ContentBlock[]>([]);
  const [submitCount, setSubmitCount] = React.useState(0);
  const [photos, setPhotos] = React.useState<File[]>([]);
  const [photoError, setPhotoError] = React.useState<string | null>(null);
  // Reading photos takes a moment: a second send meanwhile would send them twice
  const isSending = React.useRef(false);
  const [sending, setSending] = React.useState(false);
  const photoInputRef = React.useRef<HTMLInputElement>(null);
  const photoPreviews = React.useMemo(
    () => photos.map((photo) => URL.createObjectURL(photo)),
    [photos]
  );
  React.useEffect(
    () => () => {
      for (const url of photoPreviews) URL.revokeObjectURL(url);
    },
    [photoPreviews]
  );

  const addPhotos = (files: File[]) => {
    const error = chatPhotosError(files, photos.length);
    setPhotoError(error);
    if (!error) setPhotos((current) => [...current, ...files]);
  };

  const photoButton = (
    <button
      type="button"
      aria-label="Add photo"
      title="Add photo"
      onClick={() => photoInputRef.current?.click()}
      className="flex items-center justify-center rounded-full p-2 text-neutral-600 hover:bg-neutral-100"
    >
      <ImagePlus className="size-4" />
    </button>
  );

  const submitButton = (
    <button
      type="submit"
      aria-label="Send"
      disabled={sending}
      className={
        "flex items-center justify-center rounded-full bg-[#0F2449] p-2"
      }
    >
      <ArrowUp className={"size-4 text-white"} />
    </button>
  );

  const _handleSubmit = async (chatBlocks: ContentBlock[]) => {
    const chatText = chatBlocks
      .map((block) => block.text || block.label)
      .join(" ");

    const aiText = chatBlocks
      .map((block) => block.id || block.text || block.label)
      .join(" ");

    if (isSending.current) return;
    isSending.current = true;
    setSending(true);

    const sentPhotos = photos;
    let photoParts: FileUIPart[];
    try {
      photoParts = await Promise.all(sentPhotos.map(readChatPhoto));
    } catch {
      setPhotoError("The photo couldn't be read. Try adding it again.");
      return;
    } finally {
      isSending.current = false;
      setSending(false);
    }

    handleSubmit(chatText, aiText, photoParts);
    setText("");
    setInput([]);
    // Photos added while these were being read stay for the next message
    setPhotos((current) =>
      current.filter((photo) => !sentPhotos.includes(photo))
    );
    setPhotoError(null);
    setSubmitCount((prevCount) => prevCount + 1);
  };

  return (
    <PromptInput
      onSubmit={(event) => {
        event.stopPropagation();
        event.preventDefault();

        _handleSubmit(input);
      }}
      key={submitCount}
      className={cn(className, isCompact && "relative")}
    >
      <div className={cn("w-full", isCompact ? "pr-20" : "pr-10")}>
        <MentionTextarea
          suggestions={inputSuggestions || []}
          triggerChars={{ "@": "Users" }}
          value={input}
          onChange={setInput}
          onSubmit={_handleSubmit}
          initialValue={initialValue}
        />
      </div>
      <input
        ref={photoInputRef}
        type="file"
        accept={CHAT_PHOTO_TYPES.join(",")}
        multiple
        hidden
        data-testid="tessa-photo-input"
        onChange={(event) => {
          addPhotos(Array.from(event.target.files || []));
          event.target.value = "";
        }}
      />
      {(photos.length > 0 || photoError) && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2">
          {photos.map((photo, index) => (
            <div key={photoPreviews[index]} className="relative">
              <img
                src={photoPreviews[index]}
                alt={photo.name}
                className="size-14 rounded-md object-cover"
              />
              <button
                type="button"
                aria-label={`Remove ${photo.name}`}
                onClick={() =>
                  setPhotos((current) => current.filter((p) => p !== photo))
                }
                className="-top-1.5 -right-1.5 absolute flex size-5 items-center justify-center rounded-full bg-neutral-800 text-white"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
          {photoError && (
            <p role="alert" className="text-red-600 text-sm">
              {photoError}
            </p>
          )}
        </div>
      )}
      {/*<PromptInputTextarea
        onChange={(e) => setText(e.target.value)}
        value={text}
        placeholder={placeholder}
        className={"bg-white"}
      />*/}
      {isCompact && (
        <div className={"absolute top-4 right-2 flex items-center gap-1"}>
          {photoButton}
          {submitButton}
        </div>
      )}
      {!isCompact && (
        <PromptInputToolbar
          className={cn(
            showSuggestions ? "bg-[#F0F4F7]" : "",
            isCompact ? "border-t-0" : ""
          )}
        >
          <PromptInputTools>
            <div className={"mx-2 flex gap-2 py-2"}>
              {showSuggestions &&
                suggestions.map((suggestion, suggestionIndex) => (
                  <button
                    className={
                      "rounded-4xl border-1 border-neutral-100 bg-white px-3 py-1.5"
                    }
                    key={suggestionIndex}
                    onClick={() => handleSubmit(suggestion, suggestion, [])}
                  >
                    {suggestion}
                  </button>
                ))}
            </div>
          </PromptInputTools>

          <div className={"mx-2 flex items-center gap-1"}>
            {photoButton}
            {submitButton}
          </div>
        </PromptInputToolbar>
      )}
    </PromptInput>
  );
};
